'use client';

import { useCallback, useEffect, useRef } from 'react';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import customFetch from '@/lib/utils/fetch';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import { WithdrawStatus, type WithdrawRecord, type WithdrawRecordsResponse } from './use-withdraw';

// 交易历史查询（Polymarket 钱包活动）。GET /api/live-trading/history?address=0x...（需登录态）
// 代理 aipollo /polymarket/wallets/{address}/activities，页码分页（page/limit，limit 1-100 默认 50）。
const TRADING_HISTORY_PATH = '/api/live-trading/history';

export type TradingActivityType =
  | 'all'
  | 'buy'
  | 'sell'
  | 'merge'
  | 'split'
  | 'convert'
  | 'redeem'
  | 'lost'
  | 'deposit'
  | 'withdraw';

/** 活动记录（aipollo PortfolioTransactionItem） */
export interface TradingActivity {
  id: string;
  /** buy | sell | merge | split | convert | redeem | lost | deposit | withdraw */
  type: string;
  /** ISO-8601 UTC */
  timestamp: string | null;
  total_payout: number;
  market_name: string | null;
  event_name: string | null;
  group_title: string | null;
  outcome: string | null;
  outcome_name: string | null;
  shares: number | null;
  price: number | null;
  trade_value: number | null;
  fee: number | null;
  rebate: number | null;
  payout: number | null;
  usdc_received: number | null;
  shares_burned: number | null;
}

export interface TradingHistoryPagination {
  page: number;
  page_size: number;
  total: number | null;
  has_more: boolean;
}

export interface TradingHistoryMeta {
  as_of: string;
  source_tables: string[];
  tier_applied: string;
  cache_ttl: number;
  preview_applied: boolean;
  warnings?: string[];
}

export interface TradingHistoryResponse {
  data: TradingActivity[];
  pagination: TradingHistoryPagination;
  meta: TradingHistoryMeta;
}

export interface FetchTradingHistoryParams {
  address: string;
  /** 默认 'all' */
  type?: TradingActivityType;
  /** 从 1 开始，默认 1 */
  page?: number;
  /** 1-100，默认 50 */
  limit?: number;
  /** Polymarket condition ID 过滤 */
  conditionId?: string;
  /** Polymarket event ID 过滤 */
  eventId?: string | number;
  /** 是否包含充提资金记录（include_funding），默认 false */
  includeFunding?: boolean;
  /** ISO-8601 UTC */
  startTime?: string;
  /** ISO-8601 UTC */
  endTime?: string;
}

export async function fetchTradingHistory({
  address,
  type = 'all',
  page = 1,
  limit = 50,
  conditionId,
  eventId,
  includeFunding = false,
  startTime,
  endTime,
}: FetchTradingHistoryParams): Promise<TradingHistoryResponse> {
  const search = new URLSearchParams({
    address,
    type,
    page: String(page),
    limit: String(limit),
  });
  if (conditionId) search.set('condition_id', conditionId);
  if (eventId != null && eventId !== '') search.set('event_id', String(eventId));
  if (includeFunding) search.set('include_funding', String(includeFunding));
  if (startTime) search.set('start_time', startTime);
  if (endTime) search.set('end_time', endTime);

  const res = await customFetch(`${TRADING_HISTORY_PATH}?${search.toString()}`);
  if (!(res instanceof Response)) throw res instanceof Error ? res : new Error('fetch failed');
  if (!res.ok) throw new Error(`trading history request failed: ${res.status}`);
  return res.json() as Promise<TradingHistoryResponse>;
}

const WITHDRAW_APPLY_PATH = '/api/live-trading/withdraw/apply';

/** 默认混入的提现申请状态：3=提现失败,4=提现取消,5=审核拒绝 */
const DEFAULT_WITHDRAW_STATUSES = [
  WithdrawStatus.Failed,
  WithdrawStatus.Cancelled,
  WithdrawStatus.Rejected,
];

async function fetchWithdrawRecords(params: {
  limit: number;
  offset: number;
  statuses: WithdrawStatus[];
}): Promise<WithdrawRecordsResponse> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.statuses.length > 0) search.set('status', params.statuses.join(','));
  const res = await customFetch(`${WITHDRAW_APPLY_PATH}?${search.toString()}`);
  if (!(res instanceof Response)) throw res instanceof Error ? res : new Error('fetch failed');
  if (!res.ok) throw new Error(`withdraw records request failed: ${res.status}`);
  return res.json() as Promise<WithdrawRecordsResponse>;
}

/** 单页数据 = Promise.all 并发拉取两个接口；null 表示该数据源已取完（本页跳过请求） */
interface HistoryPage {
  trading: TradingHistoryResponse | null;
  withdraw: WithdrawRecordsResponse | null;
}

/** 合并记录：source 区分数据来源，sortTime（ms）为混合降序排序键 */
export type TradingHistoryRecord =
  | { source: 'activity'; sortTime: number; record: TradingActivity }
  | { source: 'withdraw'; sortTime: number; record: WithdrawRecord };

export interface UseTradingHistoryOptions
  extends Omit<FetchTradingHistoryParams, 'address' | 'page'> {
  /** 查询地址；缺省回落到系统钱包，两者都没有则不发起请求 */
  address?: string;
  /** 混入的提现申请状态，默认 [3,4,5]（失败/取消/拒绝） */
  withdrawStatuses?: WithdrawStatus[];
  /** 是否混入提现申请记录，默认 true；事件/市场维度的过滤查询建议关闭 */
  includeWithdrawals?: boolean;
  enabled?: boolean;
  swr?: SWRInfiniteConfiguration<HistoryPage, Error>;
}

/**
 * 查询交易历史（useSWRInfinite）：每页 Promise.all 并发拉取 Polymarket 钱包活动
 * （页码分页）与提现申请记录（offset 分页，默认过滤失败/取消/拒绝），两源按时间
 * 降序混合返回。任一数据源取完后后续页自动跳过该源；`loadMore` / `setSentinelRef`
 * 触发加载下一页。
 */
export function useTradingHistory(options: UseTradingHistoryOptions = {}) {
  const { wallets } = useTrackingWallets();
  const {
    type = 'all',
    limit = 50,
    conditionId = '',
    eventId = '',
    includeFunding = false,
    startTime = '',
    endTime = '',
    withdrawStatuses = DEFAULT_WITHDRAW_STATUSES,
    includeWithdrawals = true,
    enabled = true,
    swr,
  } = options;
  const address = options.address?.trim() || wallets.systemWallet;
  const statusKey = withdrawStatuses.join(',');
  const eventKey = eventId == null ? '' : String(eventId);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: HistoryPage | null) => {
      if (!enabled || !address) return null;
      // 各数据源独立判断是否还有下一页；上一页该源为 null（已跳过）视为取完
      const tradingDone =
        previousPageData != null &&
        (previousPageData.trading == null || !previousPageData.trading.pagination.has_more);
      // 关闭混入时提现源视为一开始就取完
      const withdrawDone =
        !includeWithdrawals ||
        (previousPageData != null &&
          (previousPageData.withdraw == null || !previousPageData.withdraw.pagination?.has_more));
      if (tradingDone && withdrawDone) return null;
      return [
        'trading-history',
        address,
        type,
        limit,
        conditionId,
        eventKey,
        includeFunding,
        startTime,
        endTime,
        statusKey,
        pageIndex,
        tradingDone,
        withdrawDone,
      ] as const;
    },
    [
      enabled,
      address,
      type,
      limit,
      conditionId,
      eventKey,
      includeFunding,
      startTime,
      endTime,
      statusKey,
      includeWithdrawals,
    ],
  );

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    HistoryPage,
    Error
  >(
    getKey,
    async ([
      ,
      addr,
      activityType,
      pageLimit,
      condition,
      event,
      funding,
      start,
      end,
      statuses,
      pageIndex,
      tradingDone,
      withdrawDone,
    ]: readonly [
      string,
      string,
      TradingActivityType,
      number,
      string,
      string,
      boolean,
      string,
      string,
      string,
      number,
      boolean,
      boolean,
    ]) => {
      const [trading, withdraw] = await Promise.all([
        tradingDone
          ? null
          : fetchTradingHistory({
              address: addr,
              type: activityType,
              limit: pageLimit,
              conditionId: condition || undefined,
              eventId: event || undefined,
              includeFunding: funding,
              startTime: start || undefined,
              endTime: end || undefined,
              page: pageIndex + 1,
            }),
        withdrawDone
          ? null
          : fetchWithdrawRecords({
              limit: pageLimit,
              // 提现侧每页都取满 limit，offset 随页号线性递增
              offset: pageIndex * pageLimit,
              statuses: statuses ? (statuses.split(',').map(Number) as WithdrawStatus[]) : [],
            }),
      ]);
      return { trading, withdraw };
    },
    swr,
  );

  // 两个来源打上 source/sortTime 标记后按时间降序混合
  const records: TradingHistoryRecord[] = (data ?? [])
    .flatMap((page) => [
      ...(page?.trading?.data ?? []).map((record) => ({
        source: 'activity' as const,
        sortTime: record.timestamp ? new Date(record.timestamp).getTime() : 0,
        record,
      })),
      ...(page?.withdraw?.data ?? []).map((record) => ({
        source: 'withdraw' as const,
        sortTime: new Date(record.createdAt).getTime(),
        record,
      })),
    ])
    .sort((a, b) => b.sortTime - a.sortTime);

  /** 仅交易活动（不含提现记录），已按时间降序 */
  const activities = records
    .filter(
      (item): item is Extract<TradingHistoryRecord, { source: 'activity' }> =>
        item.source === 'activity',
    )
    .map((item) => item.record);

  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage
    ? (lastPage.trading?.pagination?.has_more ?? false) ||
      (lastPage.withdraw?.pagination?.has_more ?? false)
    : false;
  const total = lastPage?.trading?.pagination?.total ?? null;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) setSize((s) => s + 1);
  }, [isValidating, hasMore, setSize]);

  // IntersectionObserver sentinel：绑定到列表底部元素，进入视口自动加载下一页
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setSentinelRef = useCallback(
    (el: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!el) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(el);
    },
    [loadMore],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    /** 两源按时间降序混合的记录（交易活动 + 提现申请） */
    records,
    activities,
    total,
    meta: lastPage?.trading?.meta ?? null,
    hasMore,
    error,
    isLoading,
    isValidating,
    page: size,
    loadMore,
    setSentinelRef,
    refresh: mutate,
  };
}

export default useTradingHistory;
